import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  Icons,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';

import { APPLY_STEP_DESCRIPTIONS } from '../../_constants';
import { useAutoOptimization } from '../../_hooks/useAutoOptimization';
import { useCanUseOptimizationWizard } from '../../_hooks/useCanUseOptimizationWizard';
import { ApplyState, ApplyStep } from '../../_types';
import { WizardStatusLayout } from './_components/WizardStatusLayout';
import { WizardTopBar } from '../wizard/WizardTopBar';

const STEP_ORDER: ApplyStep[] = ['create', 'update', 'order'];

const WORKLOAD_STEP_LABELS: Record<ApplyStep, string> = {
  create: 'Create new policies',
  update: 'Update policies',
  order: 'Re-order policies',
};

const isWorkloadStepSucceeded = (step: ApplyStep, a: ApplyState): boolean => {
  if (step === 'create') return a.currentStep !== 'create';
  if (step === 'update') return a.updatesCompleted || a.orderCompleted;
  return a.orderCompleted;
};

const ApplyStepRow = ({
  label,
  succeeded,
}: {
  label: string;
  succeeded: boolean;
}) => (
  <Stack direction="row" alignItems="center" gap={12}>
    {succeeded ? (
      <Icons.CheckCircle
        size={24}
        weight="fill"
        color={tokens.theme.bg.status.infoStrong}
      />
    ) : (
      <Icons.XCircle
        size={24}
        weight="fill"
        color={tokens.theme.bg.status.errorStrong}
      />
    )}
    <Typography variant="P14R">{label}</Typography>
  </Stack>
);

const ApplyStepList = ({ applyState }: { applyState: ApplyState }) => (
  <Box
    sx={{
      mt: 16,
      p: 16,
      borderRadius: 2,
      bgcolor: tokens.theme.bg.structural.surface,
      width: '100%',
      maxWidth: 480,
    }}
  >
    <Stack gap={12}>
      {STEP_ORDER.map((step) => (
        <ApplyStepRow
          key={step}
          label={WORKLOAD_STEP_LABELS[step]}
          succeeded={isWorkloadStepSucceeded(step, applyState)}
        />
      ))}
      {applyState.rebalance && (
        <ApplyStepRow
          label="Trigger rebalancer plan"
          succeeded={applyState.rebalance.status === 'succeeded'}
        />
      )}
      {applyState.schedule && (
        <ApplyStepRow
          label="Assign schedule to cluster"
          succeeded={applyState.schedule.status === 'succeeded'}
        />
      )}
      {applyState.evictor && (
        <ApplyStepRow
          label="Configure bin-packing"
          succeeded={applyState.evictor.status === 'succeeded'}
        />
      )}
    </Stack>
  </Box>
);

const STEP_WIDTH = 100 / STEP_ORDER.length;

// Each step fills its own third of the bar. The base target per step is
// interpolated over `STEP_DURATION_MS` and clamped by jitter so the motion
// feels organic (matching the GeneratingScreen animation pattern).
const STEP_DURATION_MS = 4000;

const stepBaseProgress = (step: ApplyStep): number => {
  const idx = STEP_ORDER.indexOf(step);
  return idx < 0 ? 0 : idx * STEP_WIDTH;
};

export const ApplyingScreen = () => {
  const { applyState, onRetryApply, onReset } = useAutoOptimization();
  const { canApplyOptimization } = useCanUseOptimizationWizard();

  const currentStep = applyState?.currentStep ?? 'create';
  const hasError =
    Boolean(applyState?.error) ||
    applyState?.rebalance?.status === 'error' ||
    applyState?.schedule?.status === 'error' ||
    applyState?.evictor?.status === 'error';

  const [progress, setProgress] = useState(() => stepBaseProgress(currentStep));
  const stepStartRef = useRef(0);

  // Animate the bar forward within the current step's range; reset timer on step change.
  useEffect(() => {
    if (hasError) return;
    stepStartRef.current = performance.now();

    const interval = setInterval(() => {
      const elapsed = performance.now() - stepStartRef.current;
      const ratio = Math.min(1, elapsed / STEP_DURATION_MS);
      const base = stepBaseProgress(currentStep) + ratio * STEP_WIDTH;
      // Jitter: -1 to +3 (forward-biased so it feels organic)
      const jitter = Math.random() * 4 - 1;
      setProgress(
        Math.min(100, Math.max(stepBaseProgress(currentStep), base + jitter))
      );
    }, 300);

    return () => clearInterval(interval);
  }, [currentStep, hasError]);

  return (
    <Stack height="100%">
      <WizardTopBar />
      {hasError ? (
        <WizardStatusLayout
          variant="error"
          title="Failed to apply"
          content={applyState && <ApplyStepList applyState={applyState} />}
          actions={
            <>
              <Button variant="secondary" size="sm" onClick={onReset}>
                Close
              </Button>
              <Button
                onClick={onRetryApply}
                disabled={!canApplyOptimization}
                size="sm"
              >
                Try again
              </Button>
            </>
          }
        />
      ) : (
        <WizardStatusLayout
          variant="castPilotAssistant2"
          title={`${Math.round(progress)}% Ready`}
          progress={Math.round(progress)}
          progressDescription={APPLY_STEP_DESCRIPTIONS[currentStep]}
        />
      )}
    </Stack>
  );
};
